import { Box } from '@mui/material';
import { Formik, FormikProps } from 'formik';
import { PoolScrollbar, ScrollToFieldError } from '@synergy/components';
import { SubPageHandle, useFormikSubPage } from '@synergy/utils';
import { forwardRef, useEffect, useRef } from 'react';
import { useParams } from 'react-router';

import * as SupplierSpecialOffersOverviewActions from './store/supplier-special-offers-detail-overview-actions';
import { PaloDirtyComparison } from '../../../../../common/components/palo/palo-dirty-context';
import { SupplierSpecialOffersDetailOverviewDates } from './components/supplier-special-offers-detail-overview-dates';
import { SupplierSpecialOffersDetailOverviewSupplierImage } from './components/supplier-special-offers-detail-overview-supplier-image';
import { SupplierSpecialOffersDetailOverviewSupplierInfo } from './components/supplier-special-offers-detail-overview-supplier-info';
import { SupplierSpecialOffersDetailOverviewText } from './components/supplier-special-offers-detail-overview-text';
import { SupplierSpecialOffersOverviewFormValues } from './models/supplier-special-offers-overview-form-values';
import { selectedLanguageSelector } from '../../../../../common/selectors/selected-language-selectors';
import {
    supplierSpecialOffersDetailOverviewBaselineSelector,
    supplierSpecialOffersDetailOverviewFormDataSelector,
} from './store/supplier-special-offers-detail-overview-selectors';
import { supplierSpecialOffersOverviewValidationSchema } from './supplier-special-offers-detail-overview-validation-schema';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export const SupplierSpecialOffersDetailOverview = forwardRef<SubPageHandle, Record<string, unknown>>((_, ref) => {
    const supplierSpecialOffer = useAppSelector(
        state => state.contentManagement.supplierSpecialOffers.detail.overview.supplierSpecialOffer
    );
    const { formValues, errors, touched } = useAppSelector(supplierSpecialOffersDetailOverviewFormDataSelector);
    // The offer as the server has it — see PaloDirtyComparison.
    const baseline = useAppSelector(supplierSpecialOffersDetailOverviewBaselineSelector);
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const dispatch = useThunkDispatch();
    const formikRef = useRef<FormikProps<SupplierSpecialOffersOverviewFormValues>>(null);
    const { id: supplierSpecialOffersIdParam } = useParams<{ id: string }>();

    useEffect(() => {
        if (!supplierSpecialOffer && supplierSpecialOffersIdParam && +supplierSpecialOffersIdParam) {
            dispatch(
                SupplierSpecialOffersOverviewActions.fetchSupplierSpecialOffersDetail(+supplierSpecialOffersIdParam)
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, supplierSpecialOffersIdParam]);

    useFormikSubPage(ref, formikRef, (values, isDirty, isValid, errs) => {
        dispatch(
            SupplierSpecialOffersOverviewActions.updateSupplierSpecialOffersOverviewDetail(
                values,
                isDirty,
                isValid,
                errs,
                selectedLanguage
            )
        );
    });

    return (
        <Formik
            initialValues={formValues}
            onSubmit={() => Promise.resolve()}
            validationSchema={supplierSpecialOffersOverviewValidationSchema}
            enableReinitialize={true}
            initialErrors={errors}
            initialTouched={touched}
            innerRef={formikRef}
        >
            <Box sx={{ px: theme => theme.general.pageContentSpacing, py: 2, flex: 1 }}>
                <PaloDirtyComparison baseline={baseline} />
                <ScrollToFieldError />
                <PoolScrollbar>
                    <SupplierSpecialOffersDetailOverviewSupplierImage />
                    <SupplierSpecialOffersDetailOverviewSupplierInfo />
                    <SupplierSpecialOffersDetailOverviewText />
                    <SupplierSpecialOffersDetailOverviewDates />
                </PoolScrollbar>
            </Box>
        </Formik>
    );
});
